import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { productService } from '../../services/products';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import Alert from '../../components/Common/Alert';
import { ImageUtils } from '../../utils/imageUtils';

const ProductList = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [productsData, categoriesData] = await Promise.all([
        productService.getMyProducts(),
        productService.getCategories()
      ]);

      setProducts(productsData.results || productsData);
      setCategories(categoriesData.results || categoriesData);
    } catch (error) {
      console.error('Error loading data:', error);
      setAlert({
        type: 'danger',
        message: 'Failed to load products. Please refresh the page.'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;

      const data = await productService.getMyProducts(params);
      setProducts(data.results || data);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadProducts();
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [loadProducts]);

  const handleDelete = async (productId, productName) => {
    if (window.confirm(`Are you sure you want to delete "${productName}"?`)) {
      try {
        await productService.deleteProduct(productId);
        setProducts(products.filter(p => p.id !== productId));
        setAlert({
          type: 'success',
          message: 'Product deleted successfully!'
        });
      } catch (error) {
        setAlert({
          type: 'danger',
          message: 'Failed to delete product. Please try again.'
        });
      }
    }
  };

  const handleToggleStatus = async (productId, currentIsActive) => {
    try {
      const newStatus = currentIsActive ? 'inactive' : 'active';
      await productService.updateProductPartial(productId, { status: newStatus });
      setProducts(products.map(p =>
        p.id === productId ? { ...p, status: newStatus } : p
      ));
      setAlert({
        type: 'success',
        message: `Product ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`
      });
    } catch (error) {
      setAlert({
        type: 'danger',
        message: 'Failed to update product status. Please try again.'
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', text: 'Active' },
      inactive: { color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)', text: 'Inactive' },
      sold_out: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', text: 'Sold Out' }
    };

    const config = statusConfig[status] || statusConfig.inactive;

    return (
      <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider" style={{ backgroundColor: config.bg, color: config.color }}>
        {config.text}
      </span>
    );
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen p-4 sm:p-6" style={{ backgroundColor: '#0a0f1e' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-1">Product Inventory</h2>
            <p className="text-[10px] sm:text-xs uppercase font-bold tracking-wider" style={{ color: '#64748b' }}>
              Manage your product catalog
            </p>
          </div>
          <Link to="/products/create" className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-bold uppercase tracking-wider transition-all inline-flex items-center justify-center gap-2" style={{ backgroundColor: '#ef4444', color: '#fff' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}>
            <i className="bi bi-plus-lg"></i>
            Add Product
          </Link>
        </div>

        {alert && (
          <div className="mb-6">
            <Alert
              type={alert.type}
              message={alert.message}
              onClose={() => setAlert(null)}
            />
          </div>
        )}

        {/* Filters */}
        <div className="rounded-2xl p-6 mb-6" style={{ backgroundColor: '#161e35' }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>Search</label>
              <div className="relative">
                <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#64748b' }}></i>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 rounded-lg text-sm outline-none"
                  style={{ backgroundColor: '#0f1420', color: '#f8fafc', border: 'none' }}
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>Category</label>
              <select
                className="w-full px-4 py-2 rounded-lg text-sm outline-none appearance-none"
                style={{ backgroundColor: '#0f1420', color: '#f8fafc', border: 'none' }}
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: '#64748b' }}>Status</label>
              <select
                className="w-full px-4 py-2 rounded-lg text-sm outline-none appearance-none"
                style={{ backgroundColor: '#0f1420', color: '#f8fafc', border: 'none' }}
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="sold_out">Sold Out</option>
              </select>
            </div>
          </div>
        </div>

        {/* Product Grid */}
        {products.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {products.map((product) => (
              <div key={product.id} className="rounded-2xl overflow-hidden transition-all hover:scale-105" style={{ backgroundColor: '#161e35' }}>
                {/* Product Image */}
                <div className="relative aspect-square overflow-hidden">
                  <img
                    src={ImageUtils.getProductImageUrl(product)}
                    className="w-full h-full object-cover"
                    alt={product.name}
                    onError={(e) => { e.target.src = ImageUtils.getPlaceholderImage(); }}
                  />

                  {/* Status Badge */}
                  <div className="absolute top-3 right-3">
                    {getStatusBadge(product.status)}
                  </div>

                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                </div>

                {/* Product Info */}
                <div className="p-4">
                  <p className="text-xs mb-1 truncate" style={{ color: '#64748b' }}>{product.category_name || 'Uncategorized'}</p>
                  <h5 className="text-base font-bold text-white mb-2 line-clamp-1">{product.name}</h5>

                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg font-bold" style={{ color: '#ef4444' }}>{formatCurrency(product.base_price)}</span>
                    <span className="text-xs font-bold" style={{ color: '#64748b' }}>Stock: {product.available_quantity}</span>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Link
                      to={`/products/${product.id}/edit`}
                      className="flex-1 py-2 rounded-lg text-xs font-bold uppercase tracking-wider text-center transition-all"
                      style={{ backgroundColor: '#0f1420', color: '#94a3b8' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#fff'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                    >
                      Edit
                    </Link>
                    <button
                      className="px-3 py-2 rounded-lg transition-all"
                      style={{ backgroundColor: '#0f1420', color: product.status === 'active' ? '#f59e0b' : '#10b981' }}
                      onClick={() => handleToggleStatus(product.id, product.status === 'active')}
                      title={product.status === 'active' ? 'Deactivate' : 'Activate'}
                    >
                      <i className={`bi ${product.status === 'active' ? 'bi-pause-fill' : 'bi-play-fill'}`}></i>
                    </button>
                    <button
                      className="px-3 py-2 rounded-lg transition-all"
                      style={{ backgroundColor: '#0f1420', color: '#64748b' }}
                      onClick={() => handleDelete(product.id, product.name)}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#64748b'}
                      title="Delete"
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-2xl p-12 text-center" style={{ backgroundColor: '#161e35' }}>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
              <i className="bi bi-box-seam text-4xl" style={{ color: '#3b82f6' }}></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">No Products Found</h3>
            <p className="text-sm mb-8 max-w-md mx-auto" style={{ color: '#64748b' }}>
              {filters.search || filters.category || filters.status
                ? 'No products match your filters. Try adjusting your search criteria.'
                : 'Start building your product catalog by adding your first product.'}
            </p>
            {!filters.search && !filters.category && !filters.status && (
              <Link to="/products/create" className="inline-flex items-center gap-2 px-8 py-3 rounded-lg text-sm font-bold uppercase tracking-wider transition-all" style={{ backgroundColor: '#ef4444', color: '#fff' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#dc2626'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ef4444'}>
                <i className="bi bi-plus-lg"></i>
                Add First Product
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductList;
